import React, {useEffect, useState} from "react";
import api from "../api/api";
import {IMAGE_URL} from "../path";
import {Link} from "react-router-dom";
import WizardCarousel from "./Components/RecipeWizard/RecipeWizardCarousel";
import SEO from "./Components/SEO";

type Steps = {
    title: string;
    description: string;
}

type SkillLevelResponse = {
    name: string;
    slug: string;
    cover_image: string;
    description: string;
}

type CuisinesResponse = {
    name: string;
    slug: string;
    cover_image: string;
    short_description: string;
}

type RecipeFilters = {
    difficulty: string;
    occasion: string;
    cuisine: string;
    protein: string;
    time: string;
    dietary_requirement: string;
    cooking_method: string;
};

type HeaderImageResponse = {
    cover_image: string;
}

type byCuisineResponse = {
    message: string;
    cover_image: HeaderImageResponse;
    difficulties: SkillLevelResponse[];
    occasions: SkillLevelResponse[];
    cuisines: CuisinesResponse[];
    proteins: SkillLevelResponse[];
    times: SkillLevelResponse[];
    dietary_preference: SkillLevelResponse[];
    cooking_method: SkillLevelResponse[];
}

type RecipeData = {
    name: string;
    slug: string;
    header_image: string;
    preparation_time: string;
    cooking_time: string;
    servings: string;
    occasions: OccasionsData[];
}

type OccasionsData = {
    name: string;
    slug: string;
}

type LetsCookFilters = Partial<RecipeFilters>;

function CookByCuisines() {
    const [loading, setLoading] = useState(false);
    const [recipeCount, setRecipeCount] = useState(0);
    const [header, setHeader] = useState<HeaderImageResponse>();

    const [difficulties, setDifficulties] = useState<SkillLevelResponse[]>([]);
    const [occasions, setOccasions] = useState<SkillLevelResponse[]>([]);
    const [cuisines, setCuisines] = useState<CuisinesResponse[]>([]);
    const [proteins, setProteins] = useState<SkillLevelResponse[]>([]);
    const [times, setTimes] = useState<SkillLevelResponse[]>([]);
    const [dietaryRequirements, setDietaryRequirements] = useState<SkillLevelResponse[]>([]);
    const [cookingMethods, setCookingMethods] = useState<SkillLevelResponse[]>([]);

    const steps: Steps[] = [
        {
            title: "Your Skill Level",
            description: "How adventurous are you today?"
        },
        {
            title: "Choose Your Occasion",
            description: "Select a meal category to narrow down our chef-curated collection."
        },
        {
            title: "Pick Your Cuisine",
            description: "What are you in the mood for?"
        },
        {
            title: "Choose Your Protein",
            description: "What would you like to cook with?"
        },
        {
            title: "Preparation Time",
            description: "How much time do you have?"
        },
        {
            title: "Dietary Preferences",
            description: "Any dietary requirements?"
        },
        {
            title: "Cooking Method",
            description: "How do you want to cook?"
        },
        {
            title: `${recipeCount} Recipes Found`,
            description: "Carefully curated for your taste.",
        }

    ]
    const [curStep, setCurStep] = useState(0);

    const visibleSteps = (() => {
        // Desktop = show everything
        if (window.innerWidth > 768) {
            return steps.map((step, index) => ({
                ...step,
                index,
            }));
        }

        // Mobile with <= 3 steps
        if (steps.length <= 3) {
            return steps.map((step, index) => ({
                ...step,
                index,
            }));
        }

        // First step
        if (curStep === 0) {
            return steps.slice(0, 3).map((step, index) => ({
                ...step,
                index,
            }));
        }

        // Last step
        if (curStep === steps.length - 1) {
            return steps.slice(-3).map((step, i) => ({
                ...step,
                index: steps.length - 3 + i,
            }));
        }

        // Middle steps
        return steps
            .slice(curStep - 1, curStep + 2)
            .map((step, i) => ({
                ...step,
                index: curStep - 1 + i,
            }));
    })();

    const [selectedDifficulty, setSelectedDifficulty] = useState<string>('');
    const [selectedOccasion, setSelectedOccasion] = useState<string>('');
    const [selectedCuisine, setSelectedCuisine] = useState<string>('');
    const [selectedProtein, setSelectedProtein] = useState<string>('');
    const [selectedTimes, setSelectedTimes] = useState<string>('');
    const [selectedDietaryRequirement, setSelectedDietaryRequirement] = useState<string>('');
    const [selectedCookingMethod, setSelectedCookingMethod] = useState<string>('');

    const [recipes, setRecipes] = useState<RecipeData[]>([]);

    async function fetchCookByCuisinesRecipes(filters: RecipeFilters) {
        try {
            setLoading(true);

            const response = await api.post(
                "/fetch-lets-cook-cuisine",
                filters
            );

            setRecipes(response.data.recipes);
            setRecipeCount(response.data.total);

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }


    const fetchLetsCook = async (filters: LetsCookFilters = {}) => {
        try {
            setLoading(true);
            const response = await api.get<byCuisineResponse>(
                "/fetch-lets-cook-cuisine",
                {
                    params: filters,
                }
            );

            setHeader(response.data.cover_image);
            setDifficulties(response.data.difficulties);
            setOccasions(response.data.occasions);
            setCuisines(response.data.cuisines);
            setProteins(response.data.proteins);
            setTimes(response.data.times);
            setDietaryRequirements(response.data.dietary_preference);
            setCookingMethods(response.data.cooking_method);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLetsCook();
    }, []);

    const chooseDifficulty = async (slug: string) => {
        setSelectedDifficulty(slug);

        await fetchLetsCook({
            difficulty: slug,
        });

        setCurStep(1);
    };

    const chooseOccasion = async (slug: string) => {
        setSelectedOccasion(slug);

        await fetchLetsCook({
            difficulty: selectedDifficulty,
            occasion: slug,
        });

        setCurStep(2);
    };

    const chooseCuisine = async (slug: string) => {
        setSelectedCuisine(slug);

        await fetchLetsCook({
            difficulty: selectedDifficulty,
            occasion: selectedOccasion,
            cuisine: slug,
        });

        setCurStep(3);
    };

    const chooseProtein = async (slug: string) => {
        setSelectedProtein(slug);

        await fetchLetsCook({
            difficulty: selectedDifficulty,
            occasion: selectedOccasion,
            cuisine: selectedCuisine,
            protein: slug,
        });

        setCurStep(4);
    };

    const chooseTime = async (slug: string) => {
        setSelectedTimes(slug);

        await fetchLetsCook({
            difficulty: selectedDifficulty,
            occasion: selectedOccasion,
            cuisine: selectedCuisine,
            protein: selectedProtein,
            time: slug,
        });

        setCurStep(5);
    };

    const chooseDietary = async (slug: string) => {
        setSelectedDietaryRequirement(slug);

        await fetchLetsCook({
            difficulty: selectedDifficulty,
            occasion: selectedOccasion,
            cuisine: selectedCuisine,
            protein: selectedProtein,
            time: selectedTimes,
            dietary_requirement: slug,
        });

        setCurStep(6);
    };

    const goToStep = async (step: number) => {
        if (step < 7) {
            setRecipes([]);
            setRecipeCount(0);
        }
        switch (step) {
            case 0:
                setSelectedDifficulty("");
                setSelectedOccasion("");
                setSelectedCuisine("");
                setSelectedProtein("");
                setSelectedTimes("");
                setSelectedDietaryRequirement("");
                setSelectedCookingMethod("");

                await fetchLetsCook();
                break;

            case 1:
                setSelectedOccasion("");
                setSelectedCuisine("");
                setSelectedProtein("");
                setSelectedTimes("");
                setSelectedDietaryRequirement("");
                setSelectedCookingMethod("");

                await fetchLetsCook({
                    difficulty: selectedDifficulty,
                });
                break;

            case 2:
                setSelectedCuisine("");
                setSelectedProtein("");
                setSelectedTimes("");
                setSelectedDietaryRequirement("");
                setSelectedCookingMethod("");

                await fetchLetsCook({
                    difficulty: selectedDifficulty,
                    occasion: selectedOccasion,
                });
                break;

            case 3:
                setSelectedProtein("");
                setSelectedTimes("");
                setSelectedDietaryRequirement("");
                setSelectedCookingMethod("");

                await fetchLetsCook({
                    difficulty: selectedDifficulty,
                    occasion: selectedOccasion,
                    cuisine: selectedCuisine,
                });
                break;

            case 4:
                setSelectedTimes("");
                setSelectedDietaryRequirement("");
                setSelectedCookingMethod("");

                await fetchLetsCook({
                    difficulty: selectedDifficulty,
                    occasion: selectedOccasion,
                    cuisine: selectedCuisine,
                    protein: selectedProtein,
                });
                break;

            case 5:
                setSelectedDietaryRequirement("");
                setSelectedCookingMethod("");

                await fetchLetsCook({
                    difficulty: selectedDifficulty,
                    occasion: selectedOccasion,
                    cuisine: selectedCuisine,
                    protein: selectedProtein,
                    time: selectedTimes,
                });
                break;

            case 6:
                setSelectedCookingMethod("");

                await fetchLetsCook({
                    difficulty: selectedDifficulty,
                    occasion: selectedOccasion,
                    cuisine: selectedCuisine,
                    protein: selectedProtein,
                    time: selectedTimes,
                    dietary_requirement: selectedDietaryRequirement,
                });
                break;
        }

        setCurStep(step);
    };

    const chooseCookingMethod = async (slug: string) => {
        setSelectedCookingMethod(slug);

        await fetchCookByCuisinesRecipes({
            difficulty: selectedDifficulty,
            occasion: selectedOccasion,
            cuisine: selectedCuisine,
            protein: selectedProtein,
            time: selectedTimes,
            dietary_requirement: selectedDietaryRequirement,
            cooking_method: slug,
        });

        setCurStep(7);
    };

    function getPage() {
        if (curStep === 0) {
            return <WizardCarousel
                items={difficulties.map((difficulty) => ({
                    cover_image: `${IMAGE_URL}${difficulty.cover_image}`,
                    name: difficulty.name,
                    description: difficulty.description,
                    slug: difficulty.slug
                }))}
                onClick={chooseDifficulty}
            />
        }
        else if (curStep === 1) {
            return <WizardCarousel
                items={occasions.map((occasion) => ({
                    cover_image: `${IMAGE_URL}${occasion.cover_image}`,
                    name: occasion.name,
                    description: occasion.description,
                    slug: occasion.slug
                }))}
                onClick={chooseOccasion}
            />
        }
        else if (curStep === 2) {
            return <WizardCarousel
                items={cuisines.map((occasion) => ({
                    cover_image: `${IMAGE_URL}${occasion.cover_image}`,
                    name: occasion.name,
                    description: occasion.short_description,
                    slug: occasion.slug
                }))}
                onClick={chooseCuisine}
            />
        }
        else if (curStep === 3) {
            return <WizardCarousel
                items={proteins.map((occasion) => ({
                    cover_image: `${IMAGE_URL}${occasion.cover_image}`,
                    name: occasion.name,
                    description: occasion.description,
                    slug: occasion.slug
                }))}
                onClick={chooseProtein}
            />
        }
        else if (curStep === 4) {
            return <WizardCarousel
                items={times.map((occasion) => ({
                    cover_image: `${IMAGE_URL}${occasion.cover_image}`,
                    name: occasion.name,
                    description: occasion.description,
                    slug: occasion.slug
                }))}
                onClick={chooseTime}
            />
        }
        else if (curStep === 5) {
            return <WizardCarousel
                items={dietaryRequirements.map((occasion) => ({
                    cover_image: `${IMAGE_URL}${occasion.cover_image}`,
                    name: occasion.name,
                    description: occasion.description,
                    slug: occasion.slug
                }))}
                onClick={chooseDietary}
            />
        }
        else if (curStep === 6) {
            return <WizardCarousel
                items={cookingMethods.map((occasion) => ({
                    cover_image: `${IMAGE_URL}${occasion.cover_image}`,
                    name: occasion.name,
                    description: occasion.description,
                    slug: occasion.slug
                }))}
                onClick={chooseCookingMethod}
            />
        }

        else if (curStep === 7 && recipes?.length === 0) {
            return <div className="no-recipes-found">
                <h2>No Recipes Found</h2>
                <p>
                    Unfortunately, we don't have any curated recipes for this
                    product yet. Please try another product or check back later.
                </p>
            </div>
        }

        else if (curStep === 7) {
            return <div
                className="recipe-wizard-recipes-page-grid"
                style={{
                    gridTemplateColumns: `repeat(${Math.min(recipes.length, 3)}, minmax(280px, 530px))`,
                }}
            >
                {recipes?.map((recipe: any) => (
                    <div className="recipes-page-card" key={recipe.id}>
                        <img
                            className="recipes-page-image"
                            src={`${IMAGE_URL}${recipe.header_image}`}
                            alt={recipe.name}
                        />

                        <div className="recipes-page-content">
                            <div className="recipe-categories-list">
                                {recipe?.occasions?.map((occasion: any, i: number) => (
                                    <div className="recipe-categories-list-item" key={i}>
                                        {occasion.name}
                                    </div>
                                ))}
                            </div>

                            <div className="recipes-page-title">
                                {recipe?.name}
                            </div>

                            <div className="home-recipe-icons">
                                <div className="recipe-icon">
                                    <div className="icon">
                                        <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
                                            <path id="Path_14082" data-name="Path 14082" d="M38.04,20.5h-3.1a.492.492,0,1,1,0-.984h3.1a.492.492,0,1,1,0,.984m-2,8.77a.487.487,0,0,1-.67.178L32.679,27.9a.49.49,0,1,1,.492-.848L35.86,28.6a.487.487,0,0,1,.178.67M29.455,4.631,27.9,7.32a.49.49,0,0,1-.848-.492l1.552-2.689a.49.49,0,0,1,.848.492m-.2,31.416a.494.494,0,0,1-.67-.178l-1.552-2.689a.487.487,0,0,1,.178-.67.494.494,0,0,1,.67.178l1.552,2.689a.489.489,0,0,1-.178.67M20.013,21.917a1.9,1.9,0,0,1-.492-3.732V7.71a.492.492,0,0,1,.984,0V18.185a1.916,1.916,0,0,1,1.349,1.349H28.7a.508.508,0,0,1,0,.984H21.854a1.894,1.894,0,0,1-1.832,1.408m.45,16.107a.492.492,0,0,1-.984,0v-3.1a.492.492,0,0,1,.984,0ZM12.793,7.481a.494.494,0,0,1-.67-.178L10.571,4.614a.487.487,0,0,1,.178-.67.494.494,0,0,1,.67.178l1.552,2.689a.489.489,0,0,1-.178.67m.153,25.683-1.552,2.689a.49.49,0,0,1-.848-.492L12.1,32.672a.49.49,0,0,1,.848.492M7.5,12.765a.487.487,0,0,1-.67.178L4.14,11.391a.49.49,0,1,1,.492-.848l2.689,1.552a.487.487,0,0,1,.178.67M7.3,27.871,4.615,29.423a.49.49,0,1,1-.492-.848l2.689-1.552a.494.494,0,0,1,.67.178.487.487,0,0,1-.178.67M1.468,19.983a.492.492,0,0,1,.492-.492h3.1a.492.492,0,1,1,0,.984H1.96a.492.492,0,0,1-.492-.492M19.529,1.959a.492.492,0,0,1,.984,0v3.1a.492.492,0,0,1-.984,0v-3.1ZM32.7,12.12l2.689-1.552a.494.494,0,0,1,.67.178.487.487,0,0,1-.178.67l-2.689,1.552a.494.494,0,0,1-.67-.178.487.487,0,0,1,.178-.67M20,0A20,20,0,1,0,40,20,20,20,0,0,0,20,0" fill="#fff"/>
                                        </svg>
                                    </div>
                                    <div className="info">
                                        {recipe.preparation_time}
                                    </div>
                                </div>
                                <div className="recipe-icon">
                                    <div className="icon">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="32.995" viewBox="0 0 50 32.995">
                                            <g id="Layer_1" data-name="Layer 1">
                                                <g id="Group_827" data-name="Group 827" >
                                                    <path id="Path_14079" data-name="Path 14079" d="M49.028,11.286a2.092,2.092,0,0,0-2.92.608,1.157,1.157,0,0,1-.928.591c-.836.127-1.671.287-2.524.43-.008-.219-.017-.405-.034-.633H7.418c-.017.228-.025.422-.042.658-1.055-.194-2.076-.371-3.089-.574-.1-.017-.186-.16-.245-.262A2.146,2.146,0,0,0,1.721,10.99,2.069,2.069,0,0,0,0,13.244a1.935,1.935,0,0,0,1.975,1.747,1.324,1.324,0,0,1,.312.042c1.511.329,3.022.658,4.524,1,.641.143.557,0,.557.675V27.955A6.7,6.7,0,0,0,14.145,34.7c7.25-.084,14.509-.025,21.759-.025a6.729,6.729,0,0,0,6.718-6.71V16.156c.177-.042.312-.084.456-.118,1.35-.3,2.7-.591,4.06-.878a4.139,4.139,0,0,1,.9-.16,1.883,1.883,0,0,0,1.637-.912,2.023,2.023,0,0,0-.658-2.794Z" transform="translate(0.008 -1.704)" fill="#fff"/>
                                                    <path id="Path_14080" data-name="Path 14080" d="M43.977,9.233a2.173,2.173,0,0,0-.3-1.418,3.251,3.251,0,0,0-1.629-1.3A31.081,31.081,0,0,0,34.2,4.574c-.937-.127-1.874-.228-2.8-.338A4.967,4.967,0,0,0,26.539,0,4.941,4.941,0,0,0,21.323,4.27c-.059.4-.971.321-1.241.321a17.588,17.588,0,0,0-4.034.49c-1.325.287-2.65.608-3.958.971l-.523.152A5.172,5.172,0,0,0,9.49,7.292a2.131,2.131,0,0,0-.734,1.95H43.969Zm-17.53-6.9a2.245,2.245,0,0,1,2.186,1.764H24.125a2.164,2.164,0,0,1,2.321-1.764Z" transform="translate(-1.355 0.002)" fill="#fff"/>
                                                </g>
                                            </g>
                                        </svg>
                                    </div>
                                    <div className="info">
                                        {recipe.cooking_time}
                                    </div>
                                </div>
                                <div className="recipe-icon">
                                    <div className="icon">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="41" viewBox="0 0 50 41">
                                            <g id="Layer_1" data-name="Layer 1" transform="translate(0 0.008)">
                                                <path id="Path_14081" data-name="Path 14081" d="M45.1,12.855a15.626,15.626,0,0,0-2.415-.992c-.572.5-1.173,1.021-1.784,1.542,3.6.982,4.456,1.994,4.456,2.3,0,.344-1.075,1.571-5.846,2.642a58.4,58.4,0,0,1-7.414,1.1L46.651,6.816c2.632-2.278,4.111-5.3,2.948-6.433s-4.3.3-6.655,2.848l-15.32,16.5c-1.282.049-2.583.079-3.914.079a75.891,75.891,0,0,1-15.8-1.463c-4.772-1.07-5.846-2.3-5.846-2.642s1.075-1.562,5.846-2.642A75.891,75.891,0,0,1,23.71,11.6a90.832,90.832,0,0,1,9.385.462c.562-.609,1.124-1.208,1.676-1.807A91.966,91.966,0,0,0,23.71,9.615,77.587,77.587,0,0,0,7.443,11.137a21.451,21.451,0,0,0-5.126,1.719C.779,13.671,0,14.633,0,15.713a2.676,2.676,0,0,0,.059.491c0-.01-.01-.02-.01-.029A22.8,22.8,0,0,0,12.3,35.434h-.02v2.318c0,1.787,5.117,3.241,11.426,3.241s11.426-1.453,11.426-3.241V35.434h-.02A22.8,22.8,0,0,0,47.37,16.175a.036.036,0,0,1-.01.029,2.09,2.09,0,0,0,.059-.491c0-1.08-.779-2.033-2.317-2.858Z" transform="translate(0 0)" fill="#fff"/>
                                            </g>
                                        </svg>

                                    </div>
                                    <div className="info">
                                        {recipe.servings}
                                    </div>
                                </div>

                            </div>


                        </div>
                        <div className="button-section">
                            <Link
                                className="navigate-recipe"
                                to={`/recipe/${recipe.slug}`}
                                state={{
                                    wizardRecipes: recipes
                                }}
                            >
                                View Recipe
                            </Link>
                        </div>
                    </div>
                ))}
            </div>
        }

        return null;
    }

    return (
        <div>
            <SEO
                title="Browse Recipes by Cuisine | DENNY"
                description="Discover DENNY recipes by cuisine, skill level, occasion, protein, dietary preference and cooking method. Find the perfect recipe for every taste and occasion."
                keywords="browse recipes by cuisine, recipe finder, DENNY recipes, cuisine recipes, cooking inspiration, meal ideas, easy recipes, dietary recipes, home cooking, DENNY South Africa"
            />
            <div className="lets-cook-by-header" style={{
                    backgroundImage: header?.cover_image
                        ? `linear-gradient(
                rgba(255,255,255,0.4),
                rgba(255,255,255,0.4)
              ),
              url(${IMAGE_URL}${header.cover_image})`
                        : `url("../assets/images/carton-background.jpg")`,
                }}>
                <div className="lets-cook-by-header-top">
                    <Link to="/" className="lets-cook-by-back-button">Back to Home</Link>
                </div>
                <div className="step-numbers">

                    {visibleSteps.map((step, index) => (

                        <div
                            key={step.index}
                            className="step-item"
                        >
                            <button
                                type="button"
                                className={`step-circle ${
                                    step.index <= curStep ? "active" : ""
                                }`}
                                onClick={() => {
                                    if (step.index <= curStep) {
                                        goToStep(step.index);
                                    }
                                }}
                            >
                                {step.index + 1}
                            </button>

                            {step.index < visibleSteps[visibleSteps.length - 1].index && (
                                <div
                                    className={`step-line ${
                                        step.index < curStep ? "active" : ""
                                    }`}
                                />
                            )}
                        </div>

                    ))}


                </div>
                <div className="step-details">

                    <h2>
                        {steps[curStep].title}
                    </h2>

                    <p>
                        {steps[curStep].description}
                    </p>

                </div>
            </div>
            <div className="cook-by-product-inner">
                {curStep > 0 && (
                    <div
                        className="back-button"
                        onClick={() => {
                            goToStep(Math.max(0, curStep - 1));
                        }}
                    >
                        {curStep === 7 ? 'Adjust Filters' : 'Go Back'}
                    </div>
                )}

                {getPage()}
                {curStep > 0 && (
                    <div className="start-over-button" onClick={() => {goToStep(0)}}>
                        Start Over
                    </div>
                )}

            </div>
        </div>
    )
}

export default CookByCuisines;
